import React, { useEffect, useState } from 'react';

// Plone's iCalendar feed of every meet (ics_view on the events folder,
// which includes meets in subfolders). The express middleware passes
// ics_view requests through to the backend.
export const MEET_CALENDAR = '/events/ics_view';

// A calendar app subscribes to webcal:// links (and keeps them updated);
// the host is only known in the browser, so the server renders a plain
// download link and the browser swaps in the webcal one after hydration.
export const SubscribeLink = ({ className, label, children }) => {
  const [href, setHref] = useState(MEET_CALENDAR);
  useEffect(() => {
    setHref(`webcal://${window.location.host}${MEET_CALENDAR}`);
  }, []);
  return (
    <a className={className} href={href} aria-label={label}>
      {children}
    </a>
  );
};

// One meet as an .ics file
export const AddToCalendarLink = ({ path, className, children }) => (
  <a className={className} href={`${path}/ics_view`} download>
    {children}
  </a>
);

// Copy text without the Clipboard API (refused in some embedded browsers
// and without permission): select it in a hidden field and copy
function legacyCopy(text) {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  field.remove();
  return ok;
}

// Google Calendar and Outlook (web) add a calendar "from URL" rather than
// following webcal:// links: copy the feed's https address for them. If the
// browser won't copy, show the address, selected, to copy by hand.
export const CopyFeedButton = ({ className }) => {
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(null);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    const url = `${window.location.origin}${MEET_CALENDAR}`;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      ok = legacyCopy(url);
    }
    if (ok) setCopied(true);
    else setManual(url);
  };

  if (manual) {
    return (
      <input
        className="asaj-copy-field"
        type="text"
        readOnly
        value={manual}
        aria-label="Meet calendar address"
        ref={(field) => field?.select()}
        onFocus={(event) => event.target.select()}
      />
    );
  }
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={copy}
        aria-label="Copy link to the meet calendar"
      >
        {copied ? 'Link copied' : 'Copy link'}
      </button>
      <span className="visually-hidden" aria-live="polite">
        {copied ? 'Meet calendar link copied' : ''}
      </span>
    </>
  );
};
